import React, { useId, useState } from 'react'
import {
  ALLOWED_SPECIAL_CHARACTERS_PASSWORD,
  MIN_PASSWORD_LENGTH,
  REGEX_PASSWORD_MIN_LENGTH,
  REGEX_SPECIAL_CHARACTERS_PASSWORD
} from 'app-constants'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import Button from 'components/button'
import Dialog from 'components/dialog'
import Field from 'components/field'
import Input from 'components/input'
import { Tick } from 'components/icons'
import { BLUE_BUTTON } from 'components/save-buttons'

export const LABEL = 'text-[13px] font-medium text-body'
export const FIELD = 'flex flex-col gap-1.5'
export const HINT = 'text-xs leading-4 text-muted'

/** What a new password needs, each with whether the one typed has it. */
const requirements = (password: string) => [
  { label: `At least ${MIN_PASSWORD_LENGTH} characters`, met: REGEX_PASSWORD_MIN_LENGTH.test(password) },
  { label: 'A lowercase letter', met: /[a-z]/.test(password) },
  { label: 'An uppercase letter', met: /[A-Z]/.test(password) },
  { label: 'A number', met: /[0-9]/.test(password) },
  {
    label: `A special character: ${ALLOWED_SPECIAL_CHARACTERS_PASSWORD}`,
    met: REGEX_SPECIAL_CHARACTERS_PASSWORD.test(password)
  }
]

/**
 * Changes the account's password, in our dialog: the current password, the new one with what it still needs
 * ticked off as it's typed, the new one again, and an MFA code when the account has multi-factor
 * authentication on. Failing, it stays open and says why.
 */
const ChangePasswordDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { user } = useAuth()
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const id = useId()
  const [current, setCurrent] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [code, setCode] = useState('')
  const [saving, setSaving] = useState(false)
  const needsCode = !!user?.is2FaEnabled

  const checks = requirements(password)
  const mismatch = confirm !== '' && confirm !== password
  const ready =
    current !== '' && checks.every((check) => check.met) && confirm === password && (!needsCode || code !== '')

  const save = async () => {
    setSaving(true)
    const twoFactorCode = needsCode ? code : undefined
    try {
      await api.updateUserAccount(current, password, confirm, twoFactorCode)
    } catch (error) {
      setSaving(false)
      displayErrorMessage(error)
      return
    }
    setSaving(false)
    setSnackbarMessage({ status: 'success', message: 'Your password was changed' })
    onClose()
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Change Password"
      description="The new password is used the next time this account signs in with its email address."
      footer={
        <>
          <Button internalType="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            internalType="outline"
            className={BLUE_BUTTON}
            isLoading={saving}
            disabled={!ready}
            onClick={save}
            data-cy="update-password-button"
          >
            Update Password
          </Button>
        </>
      }
    >
      {/* The account's email, for password managers to save the new password against. */}
      <input
        type="email"
        autoComplete="username"
        value={user?.email ?? ''}
        readOnly
        hidden
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="flex flex-col gap-5">
        <Field label="Current password" htmlFor={`${id}-current`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-current`}
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            data-cy="current-password-input"
          />
        </Field>

        <Field label="New password" htmlFor={`${id}-new`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-new`}
            type="password"
            autoComplete="new-password"
            value={password}
            aria-describedby={`${id}-requirements`}
            onChange={(event) => setPassword(event.target.value)}
            data-cy="new-password-input"
          />
          <ul id={`${id}-requirements`} className="flex flex-col gap-1" aria-label="Password requirements">
            {checks.map((check) => (
              <li
                key={check.label}
                className={`flex items-center gap-1.5 ${HINT} ${check.met ? 'text-green' : ''}`}
                aria-label={`${check.label}: ${check.met ? 'met' : 'not met'}`}
              >
                {check.met ? (
                  <Tick className="size-3.5 shrink-0" aria-hidden />
                ) : (
                  <span className="flex size-3.5 shrink-0 items-center justify-center" aria-hidden>
                    <span className="size-1 rounded-full bg-current" />
                  </span>
                )}
                {check.label}
              </li>
            ))}
          </ul>
        </Field>

        <Field label="Confirm new password" htmlFor={`${id}-confirm`} className={FIELD} labelClassName={LABEL}>
          <Input
            id={`${id}-confirm`}
            type="password"
            autoComplete="new-password"
            value={confirm}
            aria-invalid={mismatch || undefined}
            aria-describedby={mismatch ? `${id}-confirm-hint` : undefined}
            onChange={(event) => setConfirm(event.target.value)}
            data-cy="confirm-password-input"
          />
          {mismatch && (
            <p id={`${id}-confirm-hint`} className={`${HINT} text-red`}>
              The passwords do not match.
            </p>
          )}
        </Field>

        {needsCode && (
          <Field label="MFA code" htmlFor={`${id}-code`} className={FIELD} labelClassName={LABEL}>
            <Input
              id={`${id}-code`}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              aria-describedby={`${id}-code-hint`}
              onChange={(event) => setCode(event.target.value)}
            />
            <p id={`${id}-code-hint`} className={HINT}>
              The code shown in your authenticator app.
            </p>
          </Field>
        )}
      </div>
    </Dialog>
  )
}

export default ChangePasswordDialog
